import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { User } from "@supabase/supabase-js";
import { LogOut, UserRound, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { brokeredPreviewStorage } from "@/integrations/supabase/previewAuthStorage";
import { lovable } from "@/integrations/lovable/index";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { db, EntityManager, FieldInput, type Field } from "@/components/governance/EntityManager";
import { TaskList, statusTone } from "@/components/governance/TaskList";
import { ShopifyFinance } from "@/components/governance/ShopifyFinance";
import { Helmet } from "react-helmet-async";

type Member = { org_id: string; permission: "admin" | "contributor" | "reviewer" | "viewer" };

const Governance = () => {
  const [user, setUser] = useState<User | null>(null);
  const [storedAccount, setStoredAccount] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [member, setMember] = useState<Member | null>(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [authError, setAuthError] = useState("");
  const [accessError, setAccessError] = useState("");

  useEffect(() => {
    let active = true;
    const verify = async () => {
      try {
        const { data, error } = await supabase.auth.getUser();
        if (!active) return;
        setUser(data.user ?? null);
        if (error && error.name !== "AuthSessionMissingError") setAuthError(error.status && error.status >= 500 ? "The account service could not verify your sign-in. Your account is shown below, but Governance remains locked." : error.message);
        else setAuthError("");
      } catch {
        if (active) setAuthError("Unable to connect to sign-in. Please try again.");
      } finally {
        if (active) setReady(true);
      }
    };
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setStoredAccount(session?.user ?? null);
      if (!session) { setUser(null); setReady(true); }
      else window.setTimeout(() => { if (active) void verify(); }, 0);
    });
    supabase.auth.getSession().then(({ data }) => { if (active) setStoredAccount(data.session?.user ?? null); });
    void verify();
    return () => { active = false; sub.subscription.unsubscribe(); };
  }, []);

  useEffect(() => {
    let active = true;
    setMember(null); setChecked(false); setAccessError("");
    if (!user) return;
    (async () => {
      const { data: orgId, error: claimError } = await db.rpc("gov_claim_access");
      if (!active) return;
      if (claimError) { setAccessError("Your account is signed in, but Governance access could not be checked. Please reload and try again."); setChecked(true); return; }
      if (orgId) {
        const { data, error } = await db.from("gov_members").select("org_id, permission").eq("org_id", orgId).eq("user_id", user.id).maybeSingle();
        if (!active) return;
        if (error) setAccessError("Your account is signed in, but Governance access could not be checked. Please reload and try again.");
        setMember(data);
      }
      setChecked(true);
    })();
    return () => { active = false; };
  }, [user]);

  const signIn = async () => {
    setBusy(true); setAuthError("");
    try {
      const r = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: `${window.location.origin}/auth/callback`,
        extraParams: { prompt: "select_account" },
      });
      if (r.error) throw r.error;
      if (r.redirected) return;
      const { data, error } = await supabase.auth.getUser();
      if (error || !data.user) throw error ?? new Error("Sign-in did not complete. Please try again.");
      setUser(data.user);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Sign-in failed. Please try again.";
      setAuthError(message); toast.error(message);
    } finally { setBusy(false); }
  };

  const signOut = async () => {
    setBusy(true); setAuthError("");
    try {
      const { error } = await supabase.auth.signOut({ scope: "local" });
      if (error) {
        // A broken hosted account must still be removable from this browser.
        const projectRef = new URL(import.meta.env.VITE_SUPABASE_URL).hostname.split(".")[0];
        const storageKey = `sb-${projectRef}-auth-token`;
        const storage = brokeredPreviewStorage();
        await storage?.removeItem(storageKey);
        await storage?.removeItem(`${storageKey}-code-verifier`);
        window.location.replace("/governance");
        return;
      }
      setUser(null); setStoredAccount(null); setMember(null);
      toast.success("Signed out of Governance.");
    } catch {
      setAuthError("Sign-out failed. Please try again.");
      toast.error("Sign-out failed. Please try again.");
    } finally { setBusy(false); }
  };

  const account = user ?? storedAccount;
  const accountName = typeof account?.user_metadata?.full_name === "string" ? account.user_metadata.full_name : "";
  const isGoogle = account?.app_metadata?.provider === "google" || account?.identities?.some((identity) => identity.provider === "google");

  return (
    <div className="min-h-screen bg-background">
      <Helmet><title>Governance | Private</title><meta name="robots" content="noindex,nofollow" /></Helmet>
      <header className="border-b border-border">
        <div className="container mx-auto px-4 py-4 flex flex-wrap items-center justify-between gap-4">
          <Link to="/" className="font-sans font-bold">Governance</Link>
          {account ? (
            <div className="flex flex-wrap items-center gap-3 text-sm min-w-0" aria-label="Signed-in account">
              <UserRound className="h-5 w-5 text-accent shrink-0" aria-hidden="true" />
              <div className="min-w-0">
                <p className="font-sans text-xs text-muted-foreground">{!user ? "Account not verified" : isGoogle ? "Signed in with Google" : "Signed in"}</p>
                {accountName && <p className="font-sans font-medium break-words">{accountName}</p>}
                <p className="font-sans break-all">{account.email ?? "Account email unavailable"}</p>
              </div>
              <Button size="sm" variant="outline" disabled={busy} onClick={signOut}>
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LogOut className="h-4 w-4 mr-2" />}
                {busy ? "Signing out…" : "Sign out"}
              </Button>
            </div>
          ) : <Badge variant="outline">{ready ? "Not signed in" : "Checking account…"}</Badge>}
        </div>
      </header>
      <main className="container mx-auto px-4 py-8">
        {authError && <p role="alert" className="mb-4 text-destructive font-sans text-sm">{authError}</p>}
        {!ready ? <p role="status" className="text-muted-foreground">Checking account…</p> : !user ? (
          <div className="max-w-md mx-auto text-center space-y-4 py-20">
            <h1 className="font-sans text-2xl font-bold">Private governance area</h1>
            <p className="text-muted-foreground">Sign in with your authorised Google account.</p>
            <Button variant="accent" disabled={busy} onClick={signIn}>{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{busy ? "Signing in…" : "Sign in with Google"}</Button>
            <section aria-labelledby="account-recovery-title" className="border-t border-border pt-6 text-left space-y-3 font-sans text-sm">
              <h2 id="account-recovery-title" className="font-semibold text-foreground">Sign-in blocked? Here’s what to do</h2>
              <p className="text-muted-foreground">We identified an account-record error in Lovable Cloud that prevents it from verifying your sign-in. This is not a Google password problem. Governance stays locked until your account can be verified.</p>
              <ol className="list-decimal pl-5 space-y-2 text-muted-foreground">
                <li>Contact Lovable support from the Lovable editor and ask them to repair the Cloud account record for this project.</li>
                <li>Include the message below and a screenshot of the sign-in error. Do not send your password or sign-in tokens.</li>
                <li>Once support confirms the repair, use Sign out above if an account is shown, then select Sign in with Google again.</li>
              </ol>
              <p className="text-foreground break-words"><span className="font-semibold">Message for support: </span>Governance sign-in fails because Lovable Cloud account verification returns HTTP 500. The account-service logs report: “confirmation_token: converting NULL to string is unsupported”. Please repair the managed account record and confirm account verification works.</p>
              <p className="text-muted-foreground">Repeated sign-in attempts or publishing the site will not repair this account-record error.</p>
            </section>
          </div>
        ) : !checked ? (
          <p className="text-muted-foreground">Checking access…</p>
        ) : accessError ? (
          <p role="alert" className="text-destructive">{accessError}</p>
        ) : !member ? (
          <div className="max-w-md mx-auto text-center py-20 space-y-2">
            <h1 className="font-sans text-xl font-bold">No access</h1>
            <p className="text-muted-foreground">This account has not been invited. Ask an administrator to add your email.</p>
          </div>
        ) : (
          <Dashboard orgId={member.org_id} perm={member.permission} />
        )}
      </main>
    </div>
  );
};

const Dashboard = ({ orgId, perm }: { orgId: string; perm: Member["permission"] }) => {
  const isAdmin = perm === "admin";
  const canEdit = perm === "admin" || perm === "contributor" || perm === "reviewer";
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((t) => t + 1), []);

  return (
    <Tabs defaultValue="overview">
      <TabsList className="flex flex-wrap h-auto justify-start">
        {[["overview", "Overview"], ["roles", "Roles & Objectives"], ["calendar", "Calendar"], ["monthly", "Monthly Reviews"], ["annual", "Annual Governance"], ["policies", "Policies & Risks"], ["decisions", "Decisions & Documents"], ["settings", "Settings"]].map(([v, l]) => (
          <TabsTrigger key={v} value={v} className="font-sans">{l}</TabsTrigger>
        ))}
      </TabsList>
      <TabsContent value="overview"><Overview orgId={orgId} isAdmin={isAdmin} tick={tick} onRun={refresh} /></TabsContent>
      <TabsContent value="roles" className="space-y-8">
        <EntityManager table="gov_roles" orgId={orgId} title="Governance roles" canEdit={isAdmin} canDelete={isAdmin}
          fields={[{ key: "title", label: "Role", required: true, inList: true }, { key: "holder_name", label: "Accountable person", inList: true }, { key: "holder_email", label: "Email" }, { key: "deputy_name", label: "Deputy", inList: true }, { key: "appointed_on", label: "Appointed", type: "date", inList: true }, { key: "term_ends", label: "Term ends", type: "date" }, { key: "responsibilities", label: "Responsibilities", type: "textarea", inList: true }]} />
        <p className="text-xs text-muted-foreground">Holding a governance title does not grant app access. Access is managed under Settings.</p>
        <EntityManager table="gov_objectives" orgId={orgId} title="Company objectives & shareholder value measures" canEdit={canEdit} canDelete={isAdmin}
          fields={[{ key: "title", label: "Objective", required: true, inList: true }, { key: "kind", label: "Type", type: "select", options: ["financial", "non_financial"], inList: true }, { key: "measure", label: "Agreed measure", inList: true }, { key: "target", label: "Target", inList: true }, { key: "review_date", label: "Review date", type: "date", inList: true }, { key: "description", label: "Notes", type: "textarea" }]} />
        <EntityManager table="gov_management_plans" orgId={orgId} title="Management plan reviews (GM/CEO plan)" canEdit={canEdit} canDelete={isAdmin}
          fields={[{ key: "financial_year", label: "Financial year", required: true, inList: true }, { key: "plan_link", label: "Plan document link", inList: true }, { key: "objectives_summary", label: "Objectives", type: "textarea" }, { key: "proposed_budget", label: "Proposed budget", type: "textarea" }, { key: "expected_outcomes", label: "Expected value outcomes", type: "textarea" }, { key: "metrics", label: "Agreed metrics / targets", type: "textarea" }, { key: "review_comments", label: "President's review comments", type: "textarea", inList: true }, { key: "decision", label: "Decision", type: "textarea", inList: true }, { key: "follow_up", label: "Follow-up actions", type: "textarea" }, { key: "status", label: "Status", type: "select", options: ["Not started", "In progress", "Awaiting review", "Completed"], inList: true }]} />
      </TabsContent>
      <TabsContent value="calendar"><Calendar orgId={orgId} tick={tick} /></TabsContent>
      <TabsContent value="monthly">{(perm === "admin" || perm === "reviewer") && <ShopifyFinance />}<Cycles orgId={orgId} kind="monthly" canEdit={canEdit} canApprove={perm === "admin" || perm === "reviewer"} onChange={refresh} /></TabsContent>
      <TabsContent value="annual" className="space-y-8">
        <Cycles orgId={orgId} kind="annual" canEdit={canEdit} canApprove={perm === "admin" || perm === "reviewer"} onChange={refresh} />
        <EntityManager table="gov_financial_reports" orgId={orgId} title="Annual financial reporting (P&L, balance sheet, tax)" canEdit={canEdit} canDelete={isAdmin}
          fields={[{ key: "financial_period", label: "Financial period", required: true, inList: true }, { key: "report_type", label: "Report", type: "select", options: ["Income statement (P&L)", "Balance sheet", "Budget", "Tax filing"], required: true, inList: true }, { key: "document_link", label: "Statement link", inList: true }, { key: "preparer", label: "Preparer" }, { key: "reviewer", label: "Reviewer" }, { key: "audience", label: "Release audience (board / authorised shareholders)", inList: true }, { key: "published_on", label: "Released on", type: "date", inList: true }, { key: "accountant_handoff", label: "Accountant handoff notes", type: "textarea" }, { key: "filing_deadline", label: "Filing/payment deadline", type: "date" }, { key: "confirmation", label: "Filing/payment confirmation" }, { key: "status", label: "Status", type: "select", options: ["Not started", "In progress", "Awaiting review", "Completed"], inList: true }]} />
        <p className="text-xs text-muted-foreground">Release means controlled sharing with authorised recipients, not publishing on the website. Tax returns and payments are done by the external accountant; this only tracks them.</p>
      </TabsContent>
      <TabsContent value="policies" className="space-y-8">
        <Policies orgId={orgId} canEdit={canEdit} isAdmin={isAdmin} canApprove={perm === "admin" || perm === "reviewer"} />
        <EntityManager table="gov_risks" orgId={orgId} title="Risk register" canEdit={canEdit} canDelete={isAdmin} orderBy="created_at"
          fields={[{ key: "description", label: "Risk", required: true }, { key: "category", label: "Category" }, { key: "likelihood", label: "Likelihood (1-5)", type: "number" }, { key: "impact", label: "Impact (1-5)", type: "number" }, { key: "owner_name", label: "Owner" }, { key: "mitigation", label: "Mitigation", type: "textarea" }, { key: "status", label: "Status", type: "select", options: ["Open", "Mitigating", "Accepted", "Closed"] }, { key: "last_reviewed", label: "Last review", type: "date" }, { key: "next_action_date", label: "Next action date", type: "date" }]}
          render={(r) => (<>
            <div className="font-medium">{r.description} <Badge variant={r.rating >= 15 ? "destructive" : "secondary"}>Rating {r.rating}</Badge></div>
            <div className="text-muted-foreground">{r.category ?? "—"} · owner {r.owner_name ?? "—"} · {r.status} · next action {r.next_action_date ?? "—"}</div>
            {r.mitigation && <div>{r.mitigation}</div>}
          </>)} />
        <p className="text-xs text-muted-foreground">Risks rated 15 or more raise an escalation straight away.</p>
      </TabsContent>
      <TabsContent value="decisions" className="space-y-6">
        <EntityManager table="gov_decisions" orgId={orgId} title="Significant decisions (outside the annual cycle)" canEdit={canEdit} canDelete={isAdmin}
          fields={[{ key: "title", label: "Matter", required: true, inList: true }, { key: "kind", label: "Type", type: "select", options: ["Major commitment", "Ownership change", "Leadership change", "Conflict of interest", "Material risk", "Other"], inList: true }, { key: "authority", label: "Decision authority", type: "select", options: ["Board", "Shareholders / members", "Officer (delegated)"], inList: true }, { key: "approvers", label: "Approvers", inList: true }, { key: "evidence_links", label: "Evidence links", type: "textarea" }, { key: "outcome", label: "Outcome", type: "textarea", inList: true }, { key: "decided_on", label: "Decided on", type: "date", inList: true }, { key: "status", label: "Status", type: "select", options: ["Proposed", "Approved", "Rejected", "Withdrawn"], inList: true }]} />
        <div className="border border-dashed border-border rounded-md p-4">
          <div className="font-sans font-semibold">Documents <Badge variant="outline">Setup required</Badge></div>
          <p className="text-sm text-muted-foreground mt-1">Google Drive storage is coming in the next round. For now, paste document links into each task, policy or decision. Nothing is marked as saved to Drive.</p>
        </div>
      </TabsContent>
      <TabsContent value="settings"><Settings orgId={orgId} isAdmin={isAdmin} /></TabsContent>
    </Tabs>
  );
};

const today = () => new Date().toISOString().slice(0, 10);

const Overview = ({ orgId, isAdmin, tick, onRun }: { orgId: string; isAdmin: boolean; tick: number; onRun: () => void }) => {
  const [d, setD] = useState<any>(null);
  useEffect(() => {
    (async () => {
      const [org, tasks, risks, notes, cycle] = await Promise.all([
        db.from("gov_organisations").select("*").eq("id", orgId).single(),
        db.from("gov_tasks").select("id,title,due_date,status,category").eq("org_id", orgId).not("status", "in", '("Completed","Not applicable")').order("due_date"),
        db.from("gov_risks").select("*").eq("org_id", orgId).gte("rating", 15).neq("status", "Closed"),
        db.from("gov_notifications").select("*").eq("org_id", orgId).is("read_at", null).order("created_at", { ascending: false }).limit(10),
        db.from("gov_cycles").select("*").eq("org_id", orgId).eq("kind", "monthly").order("period", { ascending: false }).limit(1).maybeSingle(),
      ]);
      setD({ org: org.data, tasks: tasks.data ?? [], risks: risks.data ?? [], notes: notes.data ?? [], cycle: cycle.data });
    })();
  }, [orgId, tick]);

  const run = async () => {
    const a = await db.rpc("gov_generate_cycles", { _only_org: orgId });
    const b = await db.rpc("gov_send_reminders");
    if (a.error || b.error) return toast.error((a.error ?? b.error).message);
    toast.success(`Done: ${a.data?.created ?? 0} new cycle(s), ${b.data?.created ?? 0} reminder(s)`);
    onRun();
  };
  const markRead = async (id: string) => { await db.from("gov_notifications").update({ read_at: new Date().toISOString() }).eq("id", id); onRun(); };

  if (!d) return <p className="text-muted-foreground mt-6">Loading…</p>;
  const overdue = d.tasks.filter((t: any) => t.due_date && t.due_date < today());
  const upcoming = d.tasks.filter((t: any) => t.due_date && t.due_date >= today()).slice(0, 8);
  const awaiting = d.tasks.filter((t: any) => t.status === "Awaiting review");
  const Card = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="border border-border rounded-md p-4 bg-card"><div className="font-sans font-semibold mb-2">{title}</div><div className="text-sm space-y-1">{children}</div></div>
  );
  const list = (rows: any[]) => rows.length ? rows.map((t) => <div key={t.id}>{t.due_date} · {t.title}</div>) : <div className="text-muted-foreground">None</div>;

  return (
    <div className="space-y-4 mt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-sans text-2xl font-bold">{d.org?.display_name}</h1>
        {isAdmin && <Button size="sm" variant="outline" onClick={run}>Run now</Button>}
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card title="Next annual meeting">{d.org?.annual_meeting_date ?? <span className="text-muted-foreground">Not set — add it in Settings</span>}</Card>
        <Card title="Current monthly review">{d.cycle ? <>{d.cycle.period} · due {d.cycle.due_date} · <Badge variant={statusTone(d.cycle.status) as any}>{d.cycle.status}</Badge></> : <span className="text-muted-foreground">None yet — press Run now</span>}</Card>
        <Card title="Google Drive"><Badge variant="outline">Setup required</Badge></Card>
        <Card title={`Overdue (${overdue.length})`}>{list(overdue)}</Card>
        <Card title="Upcoming deadlines">{list(upcoming)}</Card>
        <Card title={`Awaiting review (${awaiting.length})`}>{list(awaiting)}</Card>
        <Card title="Significant risks">{d.risks.length ? d.risks.map((r: any) => <div key={r.id}>{r.description} (rating {r.rating})</div>) : <div className="text-muted-foreground">None</div>}</Card>
        <Card title="Notifications">{d.notes.length ? d.notes.map((n: any) => (
          <div key={n.id} className="flex justify-between gap-2"><span>{n.message}</span><button className="text-xs underline shrink-0" onClick={() => markRead(n.id)}>Dismiss</button></div>
        )) : <div className="text-muted-foreground">None</div>}</Card>
      </div>
    </div>
  );
};

const Calendar = ({ orgId, tick }: { orgId: string; tick: number }) => {
  const [rows, setRows] = useState<any[]>([]);
  useEffect(() => { db.from("gov_tasks").select("id,title,due_date,status,accountable_role").eq("org_id", orgId).not("due_date", "is", null).order("due_date").then(({ data }: any) => setRows(data ?? [])); }, [orgId, tick]);
  const groups: Record<string, any[]> = {};
  rows.forEach((r) => (groups[r.due_date.slice(0, 7)] ??= []).push(r));
  return (
    <div className="space-y-6 mt-4">
      {Object.keys(groups).length === 0 && <p className="text-muted-foreground">No dated tasks yet.</p>}
      {Object.entries(groups).map(([m, items]) => (
        <div key={m}><h3 className="font-sans font-semibold mb-2">{new Date(m + "-01").toLocaleDateString("en-NZ", { month: "long", year: "numeric" })}</h3>
          <div className="grid gap-1">{items.map((t) => (
            <div key={t.id} className="flex flex-wrap gap-2 text-sm border-b border-border py-1"><span className="w-24 shrink-0">{t.due_date}</span><span className="flex-1">{t.title}</span><span className="text-muted-foreground">{t.accountable_role}</span><Badge variant={statusTone(t.status) as any}>{t.status}</Badge></div>
          ))}</div></div>
      ))}
    </div>
  );
};

const Cycles = ({ orgId, kind, canEdit, canApprove, onChange }: { orgId: string; kind: "monthly" | "annual"; canEdit: boolean; canApprove: boolean; onChange: () => void }) => {
  const [cycles, setCycles] = useState<any[]>([]);
  const [sel, setSel] = useState<string>("");
  const [summary, setSummary] = useState("");
  const load = async () => {
    const { data } = await db.from("gov_cycles").select("*").eq("org_id", orgId).eq("kind", kind).order("period", { ascending: false });
    setCycles(data ?? []);
    if (!sel && data?.[0]) setSel(data[0].id);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [orgId, kind]);
  const cycle = cycles.find((c) => c.id === sel);
  useEffect(() => setSummary(cycle?.summary ?? ""), [cycle?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveSummary = async (approve: boolean) => {
    if (approve) {
      const { data: open } = await db.from("gov_tasks").select("id").eq("cycle_id", sel).not("status", "in", '("Completed","Not applicable")');
      if (open?.length) return toast.error(`${open.length} task(s) still open. Complete or mark them Not applicable first.`);
    }
    const { error } = await db.from("gov_cycles").update({ summary, ...(approve ? { status: "Completed", approved_at: new Date().toISOString() } : {}) }).eq("id", sel);
    if (error) return toast.error(error.message);
    toast.success(approve ? "Review approved" : "Summary saved");
    load(); onChange();
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-sans text-xl font-bold">{kind === "monthly" ? "Monthly reviews" : "Annual governance cycle"}</h2>
        {cycles.length > 0 && (
          <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={sel} onChange={(e) => setSel(e.target.value)}>
            {cycles.map((c) => <option key={c.id} value={c.id}>{c.period} — {c.status}</option>)}
          </select>
        )}
      </div>
      {!cycle ? (
        <p className="text-muted-foreground">{kind === "monthly" ? "No reviews yet. They're created automatically each month for the month just finished (or press Run now on Overview)." : "Set the annual meeting date in Settings, then press Run now on Overview to create the preparation tasks (60 and 30 days before, the meeting day and 14 days after)."}</p>
      ) : (<>
        <div className="text-sm text-muted-foreground">Due {cycle.due_date} {cycle.approved_at && `· approved ${new Date(cycle.approved_at).toLocaleString()}`}</div>
        <TaskList orgId={orgId} cycleId={cycle.id} canEdit={canEdit} onChange={onChange} />
        <div className="border border-border rounded-md p-4 space-y-2">
          <FieldInput field={{ key: "s", label: "Summary: exceptions, actions, owners and due dates", type: "textarea" }} value={summary} onChange={setSummary} />
          <div className="flex gap-2">
            {canEdit && cycle.status !== "Completed" && <Button size="sm" variant="outline" onClick={() => saveSummary(false)}>Save summary</Button>}
            {canApprove && cycle.status !== "Completed" && <Button size="sm" variant="accent" onClick={() => saveSummary(true)}>Approve review</Button>}
          </div>
        </div>
      </>)}
    </div>
  );
};

const Policies = ({ orgId, canEdit, isAdmin, canApprove }: { orgId: string; canEdit: boolean; isAdmin: boolean; canApprove: boolean }) => {
  const [open, setOpen] = useState<string | null>(null);
  const [versions, setVersions] = useState<any[]>([]);
  const [draft, setDraft] = useState({ content: "", document_link: "" });
  const loadV = async (pid: string) => {
    const { data } = await db.from("gov_policy_versions").select("*").eq("policy_id", pid).order("version", { ascending: false });
    setVersions(data ?? []);
  };
  const addDraft = async () => {
    const next = (versions[0]?.version ?? 0) + 1;
    const { error } = await db.from("gov_policy_versions").insert({ org_id: orgId, policy_id: open, version: next, ...draft });
    if (error) return toast.error(error.message);
    setDraft({ content: "", document_link: "" }); loadV(open!);
  };
  const approve = async (id: string) => {
    const { error } = await db.from("gov_policy_versions").update({ status: "Approved" }).eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Version approved; previous version superseded"); loadV(open!);
  };
  return (
    <div className="space-y-3">
      <EntityManager table="gov_policies" orgId={orgId} title="Policies" canEdit={canEdit} canDelete={isAdmin}
        fields={[{ key: "title", label: "Policy", required: true }, { key: "owner_name", label: "Owner" }, { key: "effective_date", label: "Effective date", type: "date" }, { key: "next_review_date", label: "Next review", type: "date" }]}
        render={(p) => (<>
          <div className="font-medium">{p.title} <Badge variant="secondary">v{p.current_version} · {p.status}</Badge></div>
          <div className="text-muted-foreground">Owner {p.owner_name ?? "—"} · effective {p.effective_date ?? "—"} · next review {p.next_review_date ?? "—"}</div>
          <button className="underline text-xs" onClick={() => { const n = open === p.id ? null : p.id; setOpen(n); if (n) loadV(n); }}>{open === p.id ? "Hide versions" : "Versions & approvals"}</button>
          {open === p.id && (
            <div className="mt-2 space-y-2">
              {versions.map((v) => (
                <div key={v.id} className="border border-border rounded p-2">
                  <div>v{v.version} · <Badge variant={v.status === "Approved" ? "default" : "outline"}>{v.status}</Badge> {v.approved_at && <span className="text-xs text-muted-foreground">approved {new Date(v.approved_at).toLocaleString()}</span>}</div>
                  {v.document_link && <a className="underline text-xs break-all" href={v.document_link} target="_blank" rel="noreferrer">{v.document_link}</a>}
                  {v.content && <p className="whitespace-pre-wrap text-xs mt-1">{v.content}</p>}
                  {canApprove && v.status === "Draft" && <Button size="sm" variant="accent" className="mt-1" onClick={() => approve(v.id)}>Approve</Button>}
                </div>
              ))}
              {canEdit && (
                <div className="space-y-2 border-t border-border pt-2">
                  <FieldInput field={{ key: "l", label: "New draft: document link" }} value={draft.document_link} onChange={(v) => setDraft((d) => ({ ...d, document_link: v }))} />
                  <FieldInput field={{ key: "c", label: "Change notes / content", type: "textarea" }} value={draft.content} onChange={(v) => setDraft((d) => ({ ...d, content: v }))} />
                  <Button size="sm" variant="outline" onClick={addDraft}>Create draft version</Button>
                </div>
              )}
            </div>
          )}
        </>)} />
      <p className="text-xs text-muted-foreground">Approved versions are locked. Changes create a new draft that needs fresh approval.</p>
    </div>
  );
};

const orgFields: Field[] = [
  { key: "display_name", label: "Organisation display name", required: true },
  { key: "legal_name", label: "Legal entity name" },
  { key: "entity_type", label: "Entity type (e.g. limited company)" },
  { key: "jurisdiction", label: "Jurisdiction" },
  { key: "currency", label: "Currency" },
  { key: "timezone", label: "Timezone" },
  { key: "fy_start_month", label: "Financial year start month (1-12)", type: "number" },
  { key: "annual_meeting_date", label: "Annual meeting date", type: "date" },
  { key: "monthly_due_day", label: "Monthly review due day (of following month)", type: "number" },
  { key: "gst_applicable", label: "GST / tax", type: "select", options: ["not_set", "applicable", "not_applicable"] },
  { key: "gst_basis", label: "Accounting basis (e.g. payments, invoice)" },
  { key: "gst_filing_period", label: "Actual filing period (e.g. two-monthly)" },
  { key: "gst_deadlines", label: "Filing / payment deadlines", type: "textarea" },
  { key: "accountant_name", label: "External accountant" },
  { key: "accountant_contact", label: "Accountant contact" },
  { key: "accountant_coordinator", label: "Person coordinating with accountant" },
  { key: "decision_rules", label: "Decision rules (notice, quorum, voting)", type: "textarea" },
];

const Settings = ({ orgId, isAdmin }: { orgId: string; isAdmin: boolean }) => {
  const [org, setOrg] = useState<any>(null);
  const [runs, setRuns] = useState<any[]>([]);
  useEffect(() => {
    db.from("gov_organisations").select("*").eq("id", orgId).single().then(({ data }: any) => setOrg(data));
    if (isAdmin) db.from("gov_job_runs").select("*").order("created_at", { ascending: false }).limit(5).then(({ data }: any) => setRuns(data ?? []));
  }, [orgId, isAdmin]);
  const save = async () => {
    const payload: any = { updated_at: new Date().toISOString() };
    orgFields.forEach((f) => (payload[f.key] = org[f.key] === "" ? null : org[f.key]));
    const { error } = await db.from("gov_organisations").update(payload).eq("id", orgId);
    if (error) toast.error(error.message); else toast.success("Settings saved");
  };
  if (!org) return null;
  return (
    <div className="space-y-8 mt-4 max-w-2xl">
      <section className="space-y-3">
        <h2 className="font-sans text-xl font-bold">Organisation setup</h2>
        {orgFields.map((f) => <FieldInput key={f.key} field={f} value={org[f.key]} onChange={(v) => setOrg((o: any) => ({ ...o, [f.key]: v }))} />)}
        {isAdmin && <Button variant="accent" onClick={save}>Save settings</Button>}
        <p className="text-xs text-muted-foreground">Reminders go out {org.reminder_days?.join(", ")} days before the due date (0 = on the day), plus overdue notices.</p>
      </section>
      <EntityManager table="gov_members" orgId={orgId} title="People with access" canEdit={isAdmin} canDelete={isAdmin}
        fields={[{ key: "email", label: "Google email", required: true, inList: true }, { key: "permission", label: "Access", type: "select", options: ["admin", "contributor", "reviewer", "viewer"], required: true, inList: true }]} />
      <p className="text-xs text-muted-foreground">Admin: everything. Contributor: edit records. Reviewer: edit and approve. Viewer: read only. Invited people sign in with that Google email.</p>
      <section className="border border-dashed border-border rounded-md p-4 space-y-1">
        <div className="font-sans font-semibold">Google Drive <Badge variant="outline">Setup required</Badge></div>
        <p className="text-sm text-muted-foreground">Connecting Drive, choosing the root folder and creating the standard folder structure is planned for the next round.</p>
      </section>
      {isAdmin && (
        <section><h3 className="font-sans font-semibold mb-2">Recent background jobs</h3>
          {runs.length ? runs.map((r) => <div key={r.id} className="text-sm">{new Date(r.created_at).toLocaleString()} · {r.job} · {r.outcome}</div>) : <p className="text-sm text-muted-foreground">No runs yet (runs hourly).</p>}
        </section>
      )}
    </div>
  );
};

export default Governance;
